import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { useAuth, useHttpClient, useOAuth, useTranslation } from '@molecule/app-react'

import { claimGuestProjects } from '../bonds/auth-default.js'
import { oauthConfig } from '../config.js'

/**
 * Detect whether this page load is an OAuth provider callback: the provider
 * appended `?code&state` AND we stashed the initiating provider in
 * sessionStorage (see `useOAuth().redirect`). Both must hold — a stray `code`
 * param without a stashed provider is not our callback and must not show the
 * overlay or run the exchange.
 *
 * @returns `true` when the current URL is an OAuth callback we initiated.
 */
function isOAuthCallback(): boolean {
  if (typeof window === 'undefined') return false
  if (!new URLSearchParams(window.location.search).has('code')) return false
  try {
    return !!sessionStorage.getItem('oauth_provider')
  } catch (_error) {
    // sessionStorage unavailable (SSR/sandbox) — treat as not-a-callback.
    return false
  }
}

/**
 * Global OAuth callback handler.
 *
 * The OAuth providers redirect back to the bare app origin (`APP_ORIGIN`) with
 * `?code&state` after consent — the API's `GET /users/oauth/:provider` sets the
 * PKCE/state httpOnly cookies and uses `redirect_uri = APP_ORIGIN`, so the
 * provider lands the user on `/` (the marketing Landing). The canonical
 * `@molecule/app-react` `useOAuth` hook completes the flow (it detects the
 * params and POSTs to `/users/log-in/oauth`), but it only runs where it is
 * mounted — and Landing mounts no auth UI. Mounting this component globally
 * (App.tsx) makes the callback complete wherever the provider lands the user.
 *
 * The `useOAuth` hook establishes the authenticated session (seeds the access
 * token + user, flips `authenticated`), so on success this handler just claims
 * any guest projects and navigates into the app — mirroring the password
 * `Login` post-success flow, including honoring a saved landing prompt. On
 * failure it sends the user to `/login` with the error so they can retry. When
 * not on a callback it renders nothing and the `useOAuth` effect is a no-op, so
 * normal visitors (incl. the lean Landing entry) are unaffected.
 *
 * @returns A transient "completing sign-in" overlay during the exchange, else `null`.
 */
export function OAuthCallback(): React.ReactElement | null {
  const navigate = useNavigate()
  const http = useHttpClient()
  const { refresh } = useAuth()
  const { t } = useTranslation()

  // Computed once: drives the transient overlay so the user sees "completing
  // sign-in" instead of a flash of the marketing page before we navigate away.
  const [isCallback] = useState(isOAuthCallback)
  // Flipped when the exchange settles (either way). This component stays
  // mounted globally across the post-auth navigation, so `isCallback` alone
  // would keep the full-screen overlay covering the app forever — the exact
  // "stuck on Completing sign-in until refresh" bug.
  const [settled, setSettled] = useState(false)

  const onSuccess = useCallback(async (): Promise<void> => {
    try {
      // The useOAuth hook has already established the authenticated session
      // (access token + user seeded, `authenticated` flipped via initialize()).
      // Refresh once more so AuthGuard definitely sees a current session (and any
      // server-side plan/profile is fresh) before we route in.
      await refresh()
      await claimGuestProjects()

      // Mirror the password Login: if the visitor started a build from the landing
      // prompt before authenticating, create the project and open it.
      const savedPrompt = sessionStorage.getItem('molecule_landing_prompt')
      if (savedPrompt) {
        sessionStorage.removeItem('molecule_landing_prompt')
        try {
          // Don't derive the project name from the raw prompt (P2-23): the backend
          // only adopts the selected template's label / imported repo's name while
          // the project is still 'Untitled'.
          const res = await http.post<{ id: string }>('/projects', {
            name: t('workspace.title.untitled', undefined, { defaultValue: 'Untitled' }),
            projectType: 'full-stack',
          })
          navigate(`/workspace/${res.data.id}`, {
            replace: true,
            state: { initialPrompt: savedPrompt },
          })
          return
        } catch (_error) {
          // Project creation failed — fall through to the dashboard rather than
          // leaving the freshly-authenticated user stranded on the callback page.
        }
      }

      navigate('/dashboard', { replace: true })
    } catch (error) {
      // refresh()/claimGuestProjects() failing must not strand the user under
      // the overlay — the session itself is already established, so route in
      // and let AuthGuard sort out any residual staleness.
      console.error('OAuth post-auth flow failed', error)
      navigate('/dashboard', { replace: true })
    } finally {
      setSettled(true)
    }
  }, [http, navigate, refresh, t])

  const onError = useCallback(
    (message: string): void => {
      setSettled(true)
      // Surface the failure on the Login page (read from location.state there)
      // so the user gets feedback instead of silently landing on the marketing page.
      navigate('/login', { replace: true, state: { oauthError: message } })
    },
    [navigate],
  )

  // Stable config (the hook's callback effect re-runs when `config` changes).
  const callbackConfig = useMemo(
    () => ({ ...oauthConfig, onSuccess, onError }),
    [onSuccess, onError],
  )

  useOAuth(callbackConfig)

  // Audit Low (2026-09-17): every settle path navigates with `replace`, but a
  // crashed or short-circuited handler could leave `?code&state` sitting in the
  // address bar (and in crash telemetry / a later refresh). Strip the query
  // defensively once the exchange has settled, while we're still on the
  // callback URL.
  useEffect(() => {
    if (!isCallback || !settled) return
    if (window.location.search) {
      window.history.replaceState(null, '', window.location.pathname)
    }
  }, [isCallback, settled])

  if (!isCallback || settled) return null

  // The Landing entry deliberately ships no ClassMap (see App.tsx), so the
  // overlay uses inline styles with the same theme variables Landing.css uses.
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--mol-color-background, #0e0e0e)',
        color: 'var(--mol-color-text, #e0e0e0)',
        font: '500 16px/1.4 system-ui, -apple-system, sans-serif',
        zIndex: 9999,
      }}
    >
      {t('oauth.completing', undefined, { defaultValue: 'Completing sign-in…' })}
    </div>
  )
}
